import React from 'react';

const LoanSummary = ({ loanData }) => {
  const formatCurrency = (amount) => {
    // Handle NaN, undefined, or null values
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '$0.00';
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const formatInterestRate = (rate) => {
    // Handle NaN, undefined, or null values
    if (rate === null || rate === undefined || isNaN(rate)) {
      return '0.0000%';
    }
    
    // Convert string to number if needed and preserve 4 decimal places
    const numericRate = typeof rate === 'string' ? parseFloat(rate) : rate;
    
    // Format to exactly 4 decimal places as entered in the form
    return `${numericRate?.toFixed(4)}%`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    // Parse date properly to avoid timezone issues
    let date;
    if (typeof dateString === 'string') {
      if (dateString?.includes('T')) {
        date = new Date(dateString.split('T')[0] + 'T12:00:00.000Z');
      } else if (dateString?.includes('-')) {
        const dateParts = dateString?.split('-');
        date = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]), 12, 0, 0);
      } else {
        date = new Date(dateString + 'T12:00:00.000Z');
      }
    } else {
      date = new Date(dateString);
    }
    
    return date?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Check if down payment date should be displayed
  const hasDownPayment = loanData?.downPayment && loanData?.downPayment > 0;
  const hasDownPaymentDate = hasDownPayment && loanData?.downPaymentDate;

  // Check if loan date should be displayed  
  const loanDate = loanData?.loanClosingDate || loanData?.loanDate || loanData?.startDate;
  const hasLoanDate = loanDate && formatDate(loanDate);

  return (
    <div className="bg-white mb-3 print:mb-2">
      <h3 className="text-lg font-bold text-gray-900 mb-2 print:text-base print:mb-1">
        Loan Summary
      </h3>
      <div className="grid grid-cols-2 gap-3 print:gap-2">
        {/* Left Column */}
        <div className="space-y-1">
          <div className="flex justify-between items-center py-0.5 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-600">Loan Amount:</span>
            <span className="text-xs font-semibold text-gray-900">
              {formatCurrency(loanData?.loanAmount || loanData?.principal || 0)}
            </span>
          </div>
          
          <div className="flex justify-between items-center py-0.5 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-600">Down Payment Amount:</span>
            <span className="text-xs font-semibold text-gray-900">
              {formatCurrency(loanData?.downPayment || 0)}
            </span>
          </div>
          
          <div className="flex justify-between items-center py-0.5 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-600">Annual Interest Rate:</span>
            <span className="text-xs font-semibold text-gray-900">
              {formatInterestRate(loanData?.annualInterestRate || loanData?.interestRate || 0)}
            </span>
          </div>
          
          {/* Only show down payment date row if there's both a payment and a date */}
          {hasDownPaymentDate && (
            <div className="flex justify-between items-center py-0.5 border-b border-gray-100">
              <span className="text-xs font-medium text-gray-600">Down Payment Date:</span>
              <span className="text-xs font-semibold text-gray-900">
                {formatDate(loanData?.downPaymentDate)}
              </span>
            </div>
          )}
          
          <div className="flex justify-between items-center py-0.5 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-600">Payment Frequency:</span>
            <span className="text-xs font-semibold text-gray-900">
              {(loanData?.paymentFrequency || 'Monthly')?.charAt(0)?.toUpperCase() + (loanData?.paymentFrequency || 'Monthly')?.slice(1)}
            </span>
          </div>
          
          <div className="flex justify-between items-center py-0.5 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-600">Total Interest Due:</span>
            <span className="text-xs font-semibold text-gray-900">
              {formatCurrency(loanData?.totalInterest || 0)}
            </span>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-1">
          <div className="flex justify-between items-center py-0.5 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-600">Number of Payments:</span>
            <span className="text-xs font-semibold text-gray-900">
              {loanData?.totalPayments || 0}
            </span>
          </div>
          
          <div className="flex justify-between items-center py-0.5 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-600">Periodic Payment:</span>
            <span className="text-xs font-semibold text-gray-900">
              {formatCurrency(loanData?.monthlyPayment || 0)}
            </span>
          </div>
          
          {/* Only show loan date row if there's actually a date value */}
          {hasLoanDate && (
            <div className="flex justify-between items-center py-0.5 border-b border-gray-100">
              <span className="text-xs font-medium text-gray-600">Loan Date:</span>
              <span className="text-xs font-semibold text-gray-900">
                {formatDate(loanDate)}
              </span>
            </div>
          )}
          
          <div className="flex justify-between items-center py-0.5 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-600">1st Payment Due:</span>
            <span className="text-xs font-semibold text-gray-900">
              {formatDate(loanData?.firstPaymentDate)}
            </span>
          </div>
          
          <div className="flex justify-between items-center py-0.5 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-600">Last Payment Due:</span>
            <span className="text-xs font-semibold text-gray-900">
              {formatDate(loanData?.finalPaymentDate || loanData?.payoffDate)}
            </span>
          </div>
          
          <div className="flex justify-between items-center py-0.5 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-600">Total All Payments:</span>
            <span className="text-xs font-semibold text-gray-900">
              {formatCurrency(loanData?.totalPayment || (loanData?.monthlyPayment * loanData?.totalPayments) || 0)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanSummary;